import { z } from 'zod';
import { OPPORTUNITY_STAGES } from './constants';

/**
 * Typed application settings stored in `app_settings` (one row per key). Every key has a zod schema with
 * defaults, so a fresh database behaves sensibly and partial/legacy values are filled in on read.
 */

const hour = z.number().int().min(0).max(24);

export const GeneralSettings = z.object({
  companyName: z.string().min(1).max(200).default('Hearth Pizza Equipment'),
  defaultCurrency: z.string().length(3).default('CAD'),
  defaultTimezone: z.string().min(1).default('America/Toronto'),
  defaultCountry: z.string().length(2).default('CA'),
  locale: z.string().default('en-CA'),
});

export const ComplianceSettings = z.object({
  /**
   * none: only suppression/DNC rules apply.
   * casl: bulk & automated email require a recorded consent basis; implied consent expires (Canada).
   * can_spam: opt-out model; requires physical address + unsubscribe (US).
   */
  mode: z.enum(['none', 'casl', 'can_spam']).default('casl'),
  senderLegalName: z.string().max(200).default(''),
  /** Physical mailing address included in every commercial email footer. */
  mailingAddress: z.string().max(500).default(''),
  unsubscribeText: z
    .string()
    .max(300)
    .default('If you would prefer not to receive further emails from us, unsubscribe here:'),
  impliedConsentInquiryMonths: z.number().int().min(1).max(60).default(6),
  impliedConsentRelationshipMonths: z.number().int().min(1).max(120).default(24),
});

export const SendingSettings = z.object({
  /** Organization-wide outbound cap per rolling 24h. */
  dailyLimit: z.number().int().min(1).max(100000).default(300),
  perUserDailyLimit: z.number().int().min(1).max(100000).default(150),
  /** Dispatcher sends at most this many emails per minute. */
  perMinuteLimit: z.number().int().min(1).max(1000).default(20),
  /** Bulk/automated email is skipped if the address received any email within this many days. */
  minDaysBetweenEmails: z.number().int().min(0).max(365).default(3),
  /** Bulk sends at or above this many recipients require typing the count to confirm. */
  bulkConfirmThreshold: z.number().int().min(1).default(25),
  sendWindow: z
    .object({
      enabled: z.boolean().default(true),
      startHour: hour.default(9),
      endHour: hour.default(17),
      /** ISO weekdays: 1 = Monday … 7 = Sunday. */
      days: z.array(z.number().int().min(1).max(7)).min(1).default([1, 2, 3, 4, 5]),
    })
    .default({ enabled: true, startHour: 9, endHour: 17, days: [1, 2, 3, 4, 5] }),
  /** Evaluate the send window in the recipient's timezone (business.timezone), else the org default. */
  respectRecipientTimezone: z.boolean().default(true),
  /** Open/click tracking are off by default for privacy; enable only where lawful and disclosed. */
  trackOpens: z.boolean().default(false),
  trackClicks: z.boolean().default(false),
  maxAttempts: z.number().int().min(1).max(10).default(3),
});

export const AutomationSettings = z.object({
  /** Global kill switch. When false no sequence enrolls or sends, regardless of sequence settings. */
  enabled: z.boolean().default(false),
  runIntervalMinutes: z.number().int().min(5).max(1440).default(15),
  maxEnrollmentsPerRun: z.number().int().min(1).max(5000).default(200),
});

export const NextActionSettings = z.object({
  quoteFollowUpDays: z.number().int().min(1).max(90).default(7),
  replyResponseHours: z.number().int().min(1).max(240).default(24),
  activeFollowUpDays: z.number().int().min(1).max(90).default(7),
  /** Tiers treated as "contact now" when never contacted. */
  priorityTierKeys: z.array(z.string()).default(['hot', 'high']),
});

export const OpportunitySettings = z.object({
  stageProbabilities: z
    .record(z.enum(OPPORTUNITY_STAGES), z.number().int().min(0).max(100))
    .default({ qualifying: 10, quote_requested: 30, quote_sent: 50, negotiation: 70, won: 100, lost: 0 }),
  lostReasons: z
    .array(z.string().min(1).max(100))
    .default(['Price', 'Chose competitor', 'No budget', 'Timing / not ready', 'No response', 'Business closed', 'Other']),
});

export const DataQualitySettings = z.object({
  staleAfterDays: z.number().int().min(30).max(3650).default(365),
  inactiveAfterDays: z.number().int().min(7).max(3650).default(180),
});

export const AccessSettings = z.object({
  /** all: reps can view every lead. own_and_unassigned: reps see only leads they own or that are unassigned. */
  repVisibility: z.enum(['all', 'own_and_unassigned']).default('all'),
  /** Which visible leads a rep may edit / email / log activity on. Managers and admins can edit all. */
  repEditScope: z.enum(['all', 'own_and_unassigned']).default('own_and_unassigned'),
});

export const DuplicateSettings = z.object({
  /** Pairs at or above this confidence are flagged POSSIBLE DUPLICATE for review. */
  possibleThreshold: z.number().int().min(1).max(100).default(55),
  /** Pairs at or above this are treated as the same business in import/discovery defaults. */
  likelyThreshold: z.number().int().min(1).max(100).default(85),
});

export const SecuritySettings = z.object({
  maxFailedLogins: z.number().int().min(3).max(50).default(5),
  lockoutMinutes: z.number().int().min(1).max(1440).default(15),
});

export type GeneralSettings = z.infer<typeof GeneralSettings>;
export type ComplianceSettings = z.infer<typeof ComplianceSettings>;
export type SendingSettings = z.infer<typeof SendingSettings>;
export type AutomationSettings = z.infer<typeof AutomationSettings>;
export type NextActionSettings = z.infer<typeof NextActionSettings>;
export type OpportunitySettings = z.infer<typeof OpportunitySettings>;
export type DataQualitySettings = z.infer<typeof DataQualitySettings>;
export type AccessSettings = z.infer<typeof AccessSettings>;
export type DuplicateSettings = z.infer<typeof DuplicateSettings>;
export type SecuritySettings = z.infer<typeof SecuritySettings>;

export const SETTINGS_SCHEMAS = {
  general: GeneralSettings,
  compliance: ComplianceSettings,
  sending: SendingSettings,
  automation: AutomationSettings,
  nextAction: NextActionSettings,
  opportunities: OpportunitySettings,
  dataQuality: DataQualitySettings,
  access: AccessSettings,
  duplicates: DuplicateSettings,
  security: SecuritySettings,
} as const;

export type SettingsKey = keyof typeof SETTINGS_SCHEMAS;
export type AppSettings = { [K in SettingsKey]: z.infer<(typeof SETTINGS_SCHEMAS)[K]> };

export const SETTINGS_KEYS = Object.keys(SETTINGS_SCHEMAS) as SettingsKey[];

/** Parse a stored value for `key`, filling defaults; invalid stored values fall back to full defaults. */
export function parseSetting<K extends SettingsKey>(key: K, value: unknown): AppSettings[K] {
  const schema = SETTINGS_SCHEMAS[key];
  const parsed = schema.safeParse(value ?? {});
  return (parsed.success ? parsed.data : schema.parse({})) as AppSettings[K];
}

export function defaultSettings(): AppSettings {
  return Object.fromEntries(SETTINGS_KEYS.map((k) => [k, parseSetting(k, {})])) as AppSettings;
}
